import { shortDate } from "@/lib/format"

// What a line means for its market's stock, worked out on the server, or
// why that cannot be said.
export type Impact =
  | { kind: "covers" | "runs_out_before"; stockout_on: string }
  | { kind: "lasts"; traced_to: string }
  | { kind: "pending" | "no_market" | "no_eta" | "no_plan" }

// One order line from the buyers' shipping schedule that has not arrived.
export interface IncomingOrderLine {
  id: string
  order_no: string | null
  product_external_id: string
  name: string | null
  sku: string | null
  quantity: number
  status: string
  etd: string | null
  // The ETA the buyers typed; arrives_on is the latest of every date they
  // gave (ETA, delays, Inventory Est, Sellable).
  eta: string | null
  arrives_on: string | null
  import_country: string | null
  warehouse_note: string | null
  market_id: string | null
  how: "country" | "warehouse" | "assumed" | null
  impact: Impact
}

export function isLate(line: IncomingOrderLine, today: string): boolean {
  return line.arrives_on !== null && line.arrives_on < today
}

export function impactText(impact: Impact, marketName: string | undefined, today: string): string {
  switch (impact.kind) {
    case "covers":
      return `Covers to ${shortDate(impact.stockout_on, today)}`
    case "runs_out_before":
      return `Stock runs out ${shortDate(impact.stockout_on, today)}, before this lands`
    case "lasts":
      return `Covers beyond ${shortDate(impact.traced_to, today)}, the last planned day`
    case "pending":
      return "Pending, not counted yet"
    case "no_market":
      return "No market: country and warehouse not matched"
    case "no_eta":
      return "No ETA"
    case "no_plan":
      return `No plan in ${marketName ?? "this market"}`
  }
}

// Lines whose order, product id, name or SKU contains the search text.
export function matchesSearch(line: IncomingOrderLine, query: string): boolean {
  const wanted = query.trim().toLowerCase()
  if (!wanted) return true
  return [line.order_no, line.product_external_id, line.name, line.sku]
    .some((text) => (text ?? "").toLowerCase().includes(wanted))
}
